import DialogAlert, { DialogRefAttrs } from '@app/components/DialogAlert';
import { useModalWithHashingControl } from '@app/components/ModalResponsive';
import OrderFinishedModal from '@app/components/OrderFinishedModal';
import { useBagContext } from '@app/contexts/Bag';
import { useClientArea } from '@app/contexts/ClientArea';
import { useClock } from '@app/contexts/Clock';
import { useRestaurantContext } from '@app/pages/Restaurant/context';
import { FavoritedMenuItemsStorage } from '@app/services/storage';
import { Address, CliqueiAcheiData, DeliveryTypesAvailable, Restaurant, User } from '@app/types';
import { getCityByUrl } from '@app/utils/city';
import { useIsTabletOrMobileScreenSize } from '@app/utils/media-query';
import { PaymentAvailable } from '@app/utils/payments';
import { useQueryParamsWithNextRouter } from '@app/utils/routing';
import { TagManager } from '@app/utils/tag-manager';
import { generateWhatsappLink, getWhatsappOrderMessage, OrderPayload } from '@app/utils/whatsapp';
import cls from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import UserInformationsManagerModal from '../../../components/UserInformationsManagerModal';
import AddressInformationBox from './AddressInformationBox';
import classes from './bag.module.css';
import BagItems from './BagItems';
import BillingInformation from './BillingInformation';
import { ClearButton, WhatsappButton } from './Buttons';
import ChangeInput from './ChangeInput';
import DeliveryTypes from './DeliveryTypes';
import DialogPaymentSelector, { DialogPaymentSelectorRefAttrs } from './Dialogs/DialogPaymentSelector';
import DialogWithChangeInput, { DialogWithChangeInputRefAttrs } from './Dialogs/DialogWithChangeInput';
import DialogChooseOptions, { DialogChooseOptionsRefAttrs } from './Dialogs/DialogWithRadioGroup';
import EmptyBag from './EmptyBag';
import {
  BagAlertsData,
  favoriteMessage,
  InsightPayload,
  selectMenuItemsNotFavorited,
  sendDeliveryInsight,
  sortItemsByMenusPosition,
  useDeliveryTypesAvailable,
  useLoadAddress,
  useLoadUser,
  usePaymentsAvailable,
} from './helpers';
import PaymentTypes from './PaymentTypes';
import PersonalDataBox from './PersonalDataBox';
import PIXClipboard from './PIXClipboard';
import SendOrderModal from './SendOrderModal';
import { useDeliveryTax } from './use-delivery-tax';

type Props = {
  restaurant: Restaurant;
  cliqueiAcheiData: CliqueiAcheiData;
  onRequestCloseMobileModal?: () => void;
  onFavoritedItems: () => void;
  shouldLogCheckoutEvent?: boolean;
};

export default function Bag({
  cliqueiAcheiData,
  restaurant,
  onRequestCloseMobileModal,
  onFavoritedItems,
  shouldLogCheckoutEvent = false,
}: Props) {
  const clock = useClock();

  const {
    timeManager,
    isAInternalMenu: isAnInternalMenu,
    isOrderedOnRestaurant,
    isAInternalMenuOrOrderedOnRestaurant,
    isMenuOnlyView,
    onlyRegisterOrder,
    tagManager,
  } = useRestaurantContext();
  const { tableNumber, counterNumber } = useQueryParamsWithNextRouter();

  const clientArea = useClientArea();

  const isTabletOrMobile = useIsTabletOrMobileScreenSize();

  const alertRef = useRef<DialogRefAttrs>(null);
  const dialogWithOptionsRef = useRef<DialogChooseOptionsRefAttrs>(null);
  const dialogWithChangeInput = useRef<DialogWithChangeInputRefAttrs>(null);
  const dialogPaymentSelector = useRef<DialogPaymentSelectorRefAttrs>(null);

  const [selectedPayment, setSelectedPayment] = useState('');
  const [selectedDeliveryType, setSelectedDeliveryType] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [isNeedOfChangeChecked, setIsNeedOfChangeChecked] = useState(true);
  const [isSendingOrderMessage, setIsSendingOrderMessage] = useState(false);
  const [isOrderFinishedModalVisible, setIsOrderFinishedModalVisible] = useState(false);
  const [orderWhatsappLink, setOrderWhatsappLink] = useState('');
  const [changeValue, setChangeValue] = useState<number | null>(null);
  const [sendOrderModalIsVisible, setSendOrderModalIsVisible] = useState(false);
  const [isPuttingUserData, setIsPuttingUserData] = useState(false);
  const [isPuttingAddress, setIsPuttingAddress] = useState(false);

  const userInformationsModal = useModalWithHashingControl('informacoes');

  const changeValueStateless = useRef(0);
  const isNeedOfChangeStateless = useRef<boolean>(true);
  const selectedPaymentStateless = useRef('');
  const selectedDeliveryTypeStateless = useRef('');

  const containerRef = useRef<HTMLDivElement>(null);
  const deliveryTypesContainerRef = useRef<HTMLElement | null>();
  const addressContainerRef = useRef<HTMLElement | null>();
  const inputChangeRef = useRef<HTMLInputElement>(null);

  const { items, totalItemsPrice, clearItems } = useBagContext();

  useLoadAddress(restaurant, cliqueiAcheiData, clientArea.isLoggedIn, setAddress);
  useLoadUser(clientArea.isLoggedIn, setUser);

  useEffect(() => {
    if (items.length === 0 && onRequestCloseMobileModal) onRequestCloseMobileModal();
  }, [items]);

  const deliveryTax = useDeliveryTax({
    restaurant,
    totalItemsPrice,
    selectedDeliveryType,
    isAnInternalMenu,
    isOrderedOnRestaurant,
    nowDate: clock.now() ?? new Date(),
  });
  const payments = usePaymentsAvailable(cliqueiAcheiData, restaurant);
  const deliveryTypes = useDeliveryTypesAvailable(cliqueiAcheiData, restaurant);

  const totalOrder = useMemo(() => {
    return totalItemsPrice + (deliveryTax ?? 0);
  }, [deliveryTax, totalItemsPrice]);

  useEffect(() => {
    if (shouldLogCheckoutEvent) tagManager?.pushInitiateCheckout(items, totalOrder);
  }, []);

  useEffect(() => {
    if (selectedPayment) tagManager?.pushPaymentInfoAdded(selectedPayment, totalOrder);
  }, [selectedPayment]);

  useEffect(() => {
    if (payments.length === 1 && selectedPayment === '') {
      setSelectedPayment(payments[0]);
    }
  }, [payments]);

  useEffect(() => {
    if (selectedPayment && !isSendingOrderMessage) {
      if (selectedPayment === PaymentAvailable.Money) {
        inputChangeRef.current?.focus();
        return;
      }

      if (isTabletOrMobile) {
        deliveryTypesContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: deliveryTypesContainerRef.current?.offsetTop ?? 0 });
      }
    }
  }, [selectedPayment]);

  useEffect(() => {
    if (selectedDeliveryType === DeliveryTypesAvailable.HomeDelivery && !isSendingOrderMessage) {
      if (isTabletOrMobile) {
        addressContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: addressContainerRef.current?.offsetTop ?? 0 });
      }
    }
  }, [selectedDeliveryType]);

  const shouldDisplayChangeInput =
    selectedPayment === PaymentAvailable.Money &&
    selectedDeliveryType !== DeliveryTypesAvailable.LocalPickup &&
    !isAInternalMenuOrOrderedOnRestaurant;

  async function handleSendOrderToWhatsappClick() {
    if (isSendingOrderMessage) return;

    setIsSendingOrderMessage(true);

    changeValueStateless.current = changeValue ?? 0;
    isNeedOfChangeStateless.current = isNeedOfChangeChecked;
    selectedPaymentStateless.current = selectedPayment;
    selectedDeliveryTypeStateless.current = selectedDeliveryType;

    if (isMenuOnlyView) {
      alertRef.current?.alert(BagAlertsData.menuViewOnly(cliqueiAcheiData.NomePuro));
      setIsSendingOrderMessage(false);
      return;
    }

    if (!restaurant.whatsapp) {
      alertRef.current?.alert(BagAlertsData.missingWhatsapp());
      setIsSendingOrderMessage(false);
      return;
    }

    const shouldIgnoreValidation = isAInternalMenuOrOrderedOnRestaurant;
    if (shouldIgnoreValidation) {
      if (openUserModalIfNecessary()) return;
      sendOrder();
      return;
    }

    if (!timeManager.isReady) {
      alertRef.current?.alert(BagAlertsData.unableDefineOpenStatus(cliqueiAcheiData.NomePuro));
      setIsSendingOrderMessage(false);
      return;
    }

    if (!timeManager.isOpen() && !clientArea.isLoggedIn) {
      alertRef.current?.alert(BagAlertsData.restaurantClosed());
      setIsSendingOrderMessage(false);
      return;
    }

    const isValidOrder = await validateOrder();
    if (!isValidOrder) {
      setIsSendingOrderMessage(false);
      return;
    }

    sendOrder();

    async function validateOrder() {
      if (payments.length === 0) {
        alertRef.current?.alert(BagAlertsData.missingPayments());
        return false;
      }

      if (deliveryTypes.length === 0) {
        alertRef.current?.alert(BagAlertsData.missingDeliveryOption());
        return false;
      }

      const deliveryTypeSelectedIsHomeDelivery = selectedDeliveryType === DeliveryTypesAvailable.HomeDelivery;

      if (totalItemsPrice < restaurant.pedidoMinimo && deliveryTypeSelectedIsHomeDelivery) {
        alertRef.current?.alert(BagAlertsData.minimumOrderValueNotSatisfied(restaurant.pedidoMinimo));
        return false;
      }

      if (!selectedPayment) {
        const response = await dialogPaymentSelector.current?.open(BagAlertsData.missingSelectPayment(payments));

        if (!response || !response.selectedPayment) return false;

        selectedPaymentStateless.current = response.selectedPayment;
        setSelectedPayment(response.selectedPayment);
        if (response.selectedPayment === PaymentAvailable.Money) {
          isNeedOfChangeStateless.current = response.isNeedOfChange;
          setIsNeedOfChangeChecked(response.isNeedOfChange);
          changeValueStateless.current = response.change ?? 0;
          setChangeValue(changeValueStateless.current);
        }
      }

      if (shouldDisplayChangeInput && isNeedOfChangeStateless.current && changeValueStateless.current === 0) {
        const buttonPressed = await alertRef.current?.choose(BagAlertsData.changeNotInformed());
        if (!buttonPressed) return false;

        if (buttonPressed.text === 'Informar troco') {
          inputChangeRef.current?.focus();
          return false;
        }

        if (buttonPressed.text === 'Não precisa de troco') {
          setIsNeedOfChangeChecked(false);
          isNeedOfChangeStateless.current = false;
        }
      }

      if (shouldDisplayChangeInput && changeValueStateless.current === totalOrder) {
        const buttonPressed = await alertRef.current?.choose(BagAlertsData.dontNeedChange(totalOrder));
        if (!buttonPressed) return false;

        if (buttonPressed.text === 'Editar') {
          inputChangeRef.current?.focus();
          setIsSendingOrderMessage(false);
          return false;
        }

        if (buttonPressed.text === 'Continuar') {
          setIsNeedOfChangeChecked(false);
          isNeedOfChangeStateless.current = false;
        }
      }

      if (shouldDisplayChangeInput && isNeedOfChangeStateless.current && changeValueStateless.current < totalOrder) {
        const dialogOptions = BagAlertsData.invalidChangeValue(totalOrder, changeValueStateless.current);
        const change = await dialogWithChangeInput.current?.open(dialogOptions);

        if (!change) return false;

        setChangeValue(change);
        changeValueStateless.current = change;
      }

      if (!selectedDeliveryType) {
        const dialogOptions = BagAlertsData.missingSelectDeliveryType(deliveryTypes);
        const deliveryTypeSelected = await dialogWithOptionsRef.current?.choose(dialogOptions);

        if (deliveryTypeSelected) {
          setSelectedDeliveryType(deliveryTypeSelected.value);
          selectedDeliveryTypeStateless.current = deliveryTypeSelected.value;
        } else {
          setIsSendingOrderMessage(false);
          return false;
        }
      }

      if (selectedDeliveryTypeStateless.current === DeliveryTypesAvailable.HomeDelivery && !address) {
        userInformationsModal.open();
        if (!user) setIsPuttingUserData(true);
        setIsPuttingAddress(true);
        setIsSendingOrderMessage(false);
        return false;
      }

      if (openUserModalIfNecessary()) return false;

      return true;
    }

    function openUserModalIfNecessary() {
      if (!user) {
        userInformationsModal.open();
        setIsPuttingUserData(true);
        setIsSendingOrderMessage(false);
        return true;
      }
      return false;
    }
  }

  async function sendOrder(orderUser?: User | null, orderAddress?: Address | null) {
    await askForSaveMenuItemNotFavorited();

    setIsSendingOrderMessage(true);

    orderUser = orderUser ?? (user as User);
    orderAddress = orderAddress ?? (address as Address);

    const restaurantCity = getCityByUrl(cliqueiAcheiData.Url_Cidade);

    if (!restaurantCity) {
      alertRef.current?.alert(BagAlertsData.cityNotFound());
      setIsSendingOrderMessage(false);
      return;
    }

    const insightPayload: InsightPayload = {
      date: Date.now(),
      city: restaurantCity,
      totalOrder: totalOrder,
      payment: selectedPaymentStateless.current,
      restaurant: {
        id: String(cliqueiAcheiData.Cod_Cliente),
        name: cliqueiAcheiData.NomePuro,
      },
      origin: clientArea.isLoggedIn ? 'web-interno' : 'web-externo',
    };

    try {
      if (process.env.NODE_ENV === 'production') await sendDeliveryInsight(insightPayload);
    } catch (error) {
      alertRef.current?.alert({ title: 'Ocorreu um erro', message: 'Não conseguimos enviar o pedido!' });
      setIsSendingOrderMessage(false);
      return;
    }

    setIsSendingOrderMessage(false);

    if (onlyRegisterOrder) {
      alertRef.current?.alert(BagAlertsData.orderRegisteredSuccessfully());
      return;
    }

    const order: OrderPayload = {
      restaurant: restaurant,
      address: orderAddress,
      changeValue: changeValueStateless.current,
      changeIsNecessary: isNeedOfChangeStateless.current,
      deliveryTax: deliveryTax,
      deliveryType: selectedDeliveryTypeStateless.current as DeliveryTypesAvailable,
      items: sortItemsByMenusPosition(items, restaurant.menus),
      orderDate: Date.now(),
      totalOrder: totalOrder,
      payment: selectedPaymentStateless.current,
      tableNumber: tableNumber,
      counterNumber: counterNumber,
    };
    const message = getWhatsappOrderMessage(order, orderUser);
    const whatsappLink = generateWhatsappLink(restaurant.whatsapp, message);
    setOrderWhatsappLink(whatsappLink);
    setSendOrderModalIsVisible(true);

    async function askForSaveMenuItemNotFavorited() {
      const notFavorited = selectMenuItemsNotFavorited(items);
      if (notFavorited.length === 0) return;

      const buttonClicked = await alertRef.current?.choose({
        title: '',
        message: favoriteMessage(notFavorited.length),
        buttons: [{ text: 'Não' }, { text: 'Sim' }],
      });

      if (buttonClicked?.text === 'Sim') saveToFavorites();

      function saveToFavorites() {
        notFavorited.forEach((item) => tagManager?.pushAddToWishlist(item));
        FavoritedMenuItemsStorage.addAll(...notFavorited.map((item) => item.id));
        onFavoritedItems?.();
      }
    }
  }

  function hadleSaveUserInformations(address: Address | null, user: User | null) {
    setAddressAndUserIfPossible();
    restartPuttingStates();
    userInformationsModal.close();
    sendOrder(user, address);

    function setAddressAndUserIfPossible() {
      if (!clientArea.isLoggedIn) {
        if (isPuttingAddress) setAddress(address);
        if (isPuttingUserData) setUser(user);
      }
    }

    function restartPuttingStates() {
      setIsPuttingUserData(false);
      setIsPuttingAddress(false);
    }
  }

  function handleInstructionsContinueClick() {
    tagManager?.pushPurchase(items, totalOrder, selectedDeliveryType as DeliveryTypesAvailable);
    setSendOrderModalIsVisible(false);
    setIsOrderFinishedModalVisible(true);
  }

  function handleChangeInputFocus(inputContainerRef: HTMLElement | null) {
    if (!containerRef.current) return;

    if (isTabletOrMobile) {
      setTimeout(() => {
        inputContainerRef?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 1000);
    } else {
      window.scrollTo({ top: inputContainerRef?.offsetTop ?? 0 });
    }
  }

  function handleMakeAnotherOrderClick() {
    setIsOrderFinishedModalVisible(false);
    clearItems();
    onRequestCloseMobileModal && onRequestCloseMobileModal();
  }

  function handleAddMoreItemsClick() {
    onRequestCloseMobileModal && onRequestCloseMobileModal();
  }

  const shouldDisplayBillingInformation = Boolean(
    !isAInternalMenuOrOrderedOnRestaurant || (isAInternalMenuOrOrderedOnRestaurant && items.length > 1)
  );

  const shouldDisplayPIXKey = selectedPayment === PaymentAvailable.PIX && !isAInternalMenuOrOrderedOnRestaurant;

  const shouldDisplayPaymentTypes = !isAInternalMenuOrOrderedOnRestaurant;
  const shouldDisplayDeliveryTypes = !isAInternalMenuOrOrderedOnRestaurant;
  const shouldDisplayWhatsappButton = !Boolean(isMenuOnlyView);

  const shouldDisplayAddressInformation = Boolean(
    selectedDeliveryType === DeliveryTypesAvailable.HomeDelivery && address && !isAInternalMenuOrOrderedOnRestaurant
  );

  const shouldDisplayPersonalInformation = Boolean(user);

  const isSendOrderButtonAvailable = Boolean(
    clientArea.isLoggedIn ||
      isAInternalMenuOrOrderedOnRestaurant ||
      (timeManager.isOpen() && selectedPayment && selectedDeliveryType)
  );

  async function handleClearButtonClick() {
    const button = await alertRef.current?.choose({
      title: 'Atenção!',
      message: 'Você tem certeza que quer limpar os itens escolhidos?',
      buttons: [{ text: 'Cancelar' }, { text: 'Limpar' }],
    });
    if (button?.text === 'Limpar') {
      clearItems();
    }
  }

  return (
    <div ref={containerRef} className={cls(classes.container)}>
      {items.length === 0 && <EmptyBag />}
      {items.length > 0 && (
        <>
          <BagItems onAddMoreItemsClick={handleAddMoreItemsClick} />
          {shouldDisplayBillingInformation && (
            <BillingInformation subtotal={totalItemsPrice} deliveryTax={deliveryTax} total={totalOrder} />
          )}

          {shouldDisplayPaymentTypes && (
            <PaymentTypes
              paymentsAvailable={payments}
              selectedPayment={selectedPayment}
              onSelectedPaymentChange={setSelectedPayment}
            />
          )}
          {shouldDisplayChangeInput && (
            <ChangeInput
              value={changeValue}
              onValueChange={setChangeValue}
              isNeedOfChange={isNeedOfChangeChecked}
              onChangeIsNeedOfChange={setIsNeedOfChangeChecked}
              inputRef={inputChangeRef}
              onInputFocus={handleChangeInputFocus}
            />
          )}
          {shouldDisplayPIXKey && <PIXClipboard restaurantConfigurations={restaurant.Configuracoes} />}
          {shouldDisplayDeliveryTypes && (
            <DeliveryTypes
              deliveryTypesAvailable={deliveryTypes}
              selectedDeliveryType={selectedDeliveryType}
              onSelectedDeliveryTypeChange={setSelectedDeliveryType}
              onContainerRef={(ref) => (deliveryTypesContainerRef.current = ref)}
            />
          )}
          {shouldDisplayPersonalInformation && <PersonalDataBox user={user} onUserChange={setUser} />}
          {shouldDisplayAddressInformation && (
            <AddressInformationBox
              address={address}
              onAddressChange={setAddress}
              onContainerRef={(ref) => (addressContainerRef.current = ref)}
            />
          )}

          {shouldDisplayWhatsappButton ? (
            <WhatsappButton
              onClick={handleSendOrderToWhatsappClick}
              disabled={!isSendOrderButtonAvailable}
              isLoading={isSendingOrderMessage}
              onlyRegisterOrder={onlyRegisterOrder}
            />
          ) : (
            <ClearButton onClick={handleClearButtonClick} disabled={true} />
          )}
        </>
      )}
      <DialogAlert ref={alertRef} />
      <DialogChooseOptions ref={dialogWithOptionsRef} />
      <DialogWithChangeInput ref={dialogWithChangeInput} />
      <DialogPaymentSelector
        ref={dialogPaymentSelector}
        restaurantConfigurations={restaurant.Configuracoes}
        minChangeValue={totalOrder}
      />
      <UserInformationsManagerModal
        address={address}
        user={user}
        onSave={hadleSaveUserInformations}
        isVisible={(isPuttingAddress || isPuttingUserData) && userInformationsModal.state}
        onClose={() => {
          userInformationsModal.close();
          setIsPuttingUserData(false);
          setIsPuttingAddress(false);
        }}
        hiddenAddressInputs={isPuttingUserData && !isPuttingAddress}
        hiddenUserInputs={isPuttingAddress && !isPuttingUserData}
        buttonLabel="Confirmar"
      />
      <SendOrderModal
        isVisible={sendOrderModalIsVisible}
        username={user?.name ?? ''}
        onContinueClick={handleInstructionsContinueClick}
        orderLink={orderWhatsappLink}
      />
      <OrderFinishedModal
        isVisible={isOrderFinishedModalVisible}
        onClose={() => setIsOrderFinishedModalVisible(false)}
        onMakeAnotherOrderClick={handleMakeAnotherOrderClick}
        orderWhatsappLink={orderWhatsappLink}
      />
    </div>
  );
}
